import { useEffect, useState } from 'react'
import type { UserOption } from '@/features/preinvest/types'
import { apiGet } from '@/lib/api'

/**
 * Module-level cache so the board resolves the team **once** for every card,
 * not per-card. The first caller kicks off the `GET /users?role=team,admin`
 * fetch (the same source `OwnerSelect` uses) and every later caller reuses the
 * settled promise.
 */
let cache: Promise<Map<string, UserOption>> | null = null

function loadTeam(): Promise<Map<string, UserOption>> {
  if (!cache) {
    cache = apiGet<UserOption[]>('/api/v1/users?role=team,admin')
      .then((data) => {
        // Defensive: only accept an array; anything else degrades to "empty"
        // (mirrors OwnerSelect).
        const members = Array.isArray(data) ? data : []
        return new Map(members.map((member) => [member.id, member]))
      })
      .catch(() => new Map<string, UserOption>())
  }
  return cache
}

/**
 * Resolve team members by id for the board cards. Returns `membersById: null`
 * while the (shared) fetch is in flight, and a populated — possibly empty — map
 * once it has settled.
 */
export function useTeamMembers(): { membersById: Map<string, UserOption> | null } {
  const [membersById, setMembersById] = useState<Map<string, UserOption> | null>(null)

  useEffect(() => {
    let cancelled = false
    loadTeam().then((members) => {
      if (!cancelled) setMembersById(members)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return { membersById }
}
